import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlayerContext } from '../PlayerContext';
import { useStore } from '../hooks/useStore';
import { formatTime } from './formatTime';
import styles from './Timeline.module.css';

export function Timeline() {
  const { store, eventBus } = usePlayerContext();
  const currentTime = useStore(store, useCallback(s => s.currentTime, []));
  const duration = useStore(store, useCallback(s => s.duration, []));

  const trackRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const [isSeeking, setIsSeeking] = useState(false);
  const [seekPreview, setSeekPreview] = useState(0);
  const [pendingSeek, setPendingSeek] = useState<number | null>(null);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverX, setHoverX] = useState(0);

  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (pendingSeek === null) return;
    if (Math.abs(currentTime - pendingSeek) <= 0.75) {
      setPendingSeek(null);
    }
  }, [currentTime, pendingSeek]);

  const getTimeFromClientX = useCallback((clientX: number): number => {
    if (!trackRef.current || duration <= 0) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * duration;
  }, [duration]);

  const emitSeek = useCallback((time: number) => {
    setPendingSeek(time);
    eventBus.emit('seek:to', time);
  }, [eventBus]);

  const handleTrackDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const time = getTimeFromClientX(e.clientX);
    setIsSeeking(true);
    setSeekPreview(time);
    emitSeek(time);

    const onMove = (ev: MouseEvent) => {
      const t = getTimeFromClientX(ev.clientX);
      setSeekPreview(t);
      emitSeek(t);
    };
    const cleanup = () => {
      setIsSeeking(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', cleanup);
      dragCleanupRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', cleanup);
    dragCleanupRef.current = cleanup;
  }, [getTimeFromClientX, emitSeek]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;
    const time = getTimeFromClientX(touch.clientX);
    setIsSeeking(true);
    setSeekPreview(time);
    emitSeek(time);

    const onTouchMove = (ev: TouchEvent) => {
      ev.preventDefault();
      const t = ev.touches[0];
      if (t) {
        const seekTime = getTimeFromClientX(t.clientX);
        setSeekPreview(seekTime);
        emitSeek(seekTime);
      }
    };
    const cleanup = () => {
      setIsSeeking(false);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', cleanup);
      document.removeEventListener('touchcancel', cleanup);
      dragCleanupRef.current = null;
    };
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', cleanup);
    document.addEventListener('touchcancel', cleanup);
    dragCleanupRef.current = cleanup;
  }, [getTimeFromClientX, emitSeek]);

  const handleTrackHover = useCallback((e: React.MouseEvent) => {
    if (!trackRef.current || duration <= 0) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    setHoverX(x);
    setHoverTime(getTimeFromClientX(e.clientX));
  }, [duration, getTimeFromClientX]);

  const handleTrackLeave = useCallback(() => {
    setHoverTime(null);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (duration <= 0) return;
    const seekStep = e.shiftKey ? 10 : 5;
    const base = isSeeking ? seekPreview : (pendingSeek ?? currentTime);

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      emitSeek(Math.max(0, base - seekStep));
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      emitSeek(Math.min(duration, base + seekStep));
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      emitSeek(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      emitSeek(duration);
    }
  }, [duration, isSeeking, seekPreview, pendingSeek, currentTime, emitSeek]);


  const hasDuration = duration > 0;
  const displayTime = isSeeking ? seekPreview : (pendingSeek ?? currentTime);
  const playedRatio = hasDuration ? Math.min(displayTime / duration, 1) : 0;
  const playedPct = `${playedRatio * 100}%`;
  // White 50% segment from the knob to the cursor when hovering ahead of it
  const showPreview = hoverTime !== null && hasDuration && hoverTime > displayTime;
  const previewRatio = showPreview ? Math.min(hoverTime / duration, 1) : 0;
  const previewPct = `${(previewRatio - playedRatio) * 100}%`;

  return (
    <div className={styles.timeline}>
      <div
        ref={trackRef}
        className={styles.track}
        role="slider"
        aria-label="Video timeline"
        aria-valuemin={0}
        aria-valuemax={Math.floor(duration)}
        aria-valuenow={Math.floor(displayTime)}
        tabIndex={0}
        onMouseDown={handleTrackDown}
        onTouchStart={handleTouchStart}
        onKeyDown={handleKeyDown}
        onMouseMove={handleTrackHover}
        onMouseLeave={handleTrackLeave}
        data-seeking={isSeeking || undefined}
      >
        <div className={styles.played} style={{ width: playedPct }} />
        {showPreview && (
          <div className={styles.preview} style={{ left: playedPct, width: previewPct }} />
        )}
        <div className={styles.thumb} style={{ left: playedPct }} />
        {hoverTime !== null && (
          <div className={styles.tooltip} style={{ left: hoverX }}>
            {formatTime(hoverTime)}
          </div>
        )}
      </div>
    </div>
  );
}
